/*

Class for generating a eoc clinical impression

http://www.hl7.org/fhir/clinicalimpression.html

{
  "state" : "<code>" // active || pending || archive || delete
  "eocId" : "<String>"
  "clinicalImpressionId" : "<String>"
  "stateReason" : { code }, // Reason for current status
  "code" : { code }, // Kind of assessment performed
  "description" : "<string>", // Why/how the assessment was performed
  "subject" : { Reference(Patient|Group) }, // R!  Patient or group assessed

  // effective[x]: Time of assessment. One of these 2:
  "effectiveDateTime" : "<dateTime>",
  "effectivePeriod" : { Period },

  "assessor" : { Reference(Practitioner|PractitionerRole) }, // The clinician performing the assessment
  "previous" : { Reference(ClinicalImpression) }, // Reference to last assessment
  "problem" : [{ Reference(Condition|AllergyIntolerance) }], // Relevant impressions of patient state
  "investigation" : [{ // One or more sets of investigations (signs, symptoms, etc.)
    "code" : { code }, // R!  A name/code for the set
    "item" : [{ Reference(Observation|QuestionnaireResponse|FamilyMemberHistory|
    DiagnosticReport|RiskAssessment|ImagingStudy|Media) }] // Record of a specific investigation
  }],
  "protocol" : ["<uri>"], // Clinical Protocol followed
  "summary" : "<string>", // Summary of the assessment
  "finding" : [{ // Possible or likely findings and diagnoses
    "itemcode" : { code }, // What was found
    "itemReference" : { Reference(Condition|Observation|Media) }, // What was found
    "basis" : "<string>" // Which investigations support finding
  }],
  "prognosiscode" : [{ code }], // Estimate of likely outcome
  "prognosisReference" : [{ Reference(RiskAssessment) }], // RiskAssessment expressing likely outcome
  "supportingInfo" : [{ Reference(Any) }], // Information supporting the clinical impression
  "note" : [{ Annotation }] // Comments made about the ClinicalImpression
}

*/

// Import libraries
const Joi = require('joi');

// Import variables
const { STATES } = require('@bcm/constants');
const { DataTableBase } = require('./base.class');
const { joiValidateObj, identifiers } = require('../datastructComponents');
const { investigationSchema } = require('./investigation.class');
const { findingSchema } = require('./finding.class');
const { codeSchema } = require('./code.class');

// Create schema
const schema = {
  identifier: Joi.any().optional(),
  state: Joi.string().valid( ...Object.keys(STATES) ).required(),
  eocId: Joi.string().required(),
  clinicalImpressionId: Joi.string().required(),
  stateReason: Joi.object().keys( codeSchema ).optional(),
  code: Joi.object().keys( codeSchema ).optional(),
  description: Joi.string().optional(),
  subject: Joi.string().optional(),
  effectiveDateTime: Joi.string().optional(),
  effectivePeriodStart: Joi.string().optional(),
  effectivePeriodEnd: Joi.string().optional(),
  assessor: Joi.string().optional(),
  previous: Joi.string().optional(),
  problem: Joi.array().items( Joi.string().optional() ),
  investigation: Joi.array().items(investigationSchema).optional(),
  protocol: Joi.array().items( Joi.string().optional() ),
  summary: Joi.string().optional(),
  finding: Joi.array().items(findingSchema).optional(),
  prognosiscode: Joi.array().items(codeSchema).optional(),
  prognosisReference: Joi.array().items( Joi.string().optional() ),
  supportingInfo: Joi.array().items( Joi.string().optional() ),
  note: Joi.array().items( Joi.string().optional() ).optional(), // Link to annotations
};

// Define class
class ClinicalImpression extends DataTableBase {
  constructor ( obj ) {
    joiValidateObj(schema, obj);
    super(
      identifiers.clinicalImpression.buildPk(obj.eocId),
      identifiers.clinicalImpression.buildSk(obj.state, obj.eocId, obj.clinicalImpressionId)
    );
    for (const key of Object.keys(obj)) {
      if(obj[key] || obj[key] === false) {
        this[key] = obj[key];
      }
    }
  }
}

exports.ClinicalImpression = ClinicalImpression;
exports.clinicalImpressionSchema = schema;
